import React from 'react';
import { ClockIcon, MapPinIcon } from 'lucide-react';
import { Container, Section } from '../ui/Section';
import { ScriptEyebrow } from '../ui/ScriptEyebrow';
import { NewTabHint } from '../ui/NewTabHint';
import { PhotoFrame } from '../ui/PhotoFrame';
import { Reveal, stagger } from '../motion/Reveal';
import { FloatingPhoto } from '../motion/FloatingPhoto';
import { DoodleSquiggle } from '../brand/Doodles';
import { DoodleScatter } from '../brand/DoodleScatter';
import { findVisibleRecurring, typicalWeekCards } from '../../data/recurring';
import { typicalWeekPhoto } from '../../data/photos';
const labelAccents: Record<string, string> = {
  'weekly-worship': 'bg-brand-yellow',
  'community-groups': 'bg-brand-blue',
  'sports-nights': 'bg-brand-pink'
};

/**
 * Permanent, low-maintenance overview of a normal BCM week. Every schedule and
 * location comes from data/recurring.ts, and only confirmed records render.
 */
export function TypicalWeek({
  showPhoto = true
}: {
  showPhoto?: boolean;
}) {
  return <Section className="relative overflow-hidden bg-[#fffdf6] py-16 sm:py-20" labelledBy="typical-week-heading" data-id="element-306" data-mp="28efykbnl7pr0">
      <DoodleScatter tone="white" seed="typical-week" data-id="element-307" data-mp="2cdf3vxd8doiz" />
      <Container className="relative" data-id="element-308" data-mp="2h5geleur376w">
        <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.6fr] lg:gap-12" data-id="element-309" data-mp="2h6hx24e3ivyn">
          <Reveal data-id="element-310" data-mp="2m3q9rpgh67si">
            <ScriptEyebrow accent="var(--bcm-dark-blue)" data-id="element-311" data-mp="2m49okxatwg2b">Find your rhythm</ScriptEyebrow>
            <h2 id="typical-week-heading" className="mt-3 font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-[2.6rem]" data-id="element-312" data-mp="1pgd7zsfe5kr3">
              A typical week at BCM
            </h2>
            <p className="mt-4 max-w-2xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-313" data-mp="23ejw4gujg6dk">
              There is more than one way to get involved with UCF BCM. Weekly activities can change
              by semester, so check our official channels for the latest times, locations, and
              special events.
            </p>
          </Reveal>

          {showPhoto ? <Reveal x={16} delay={0.12} className="relative mx-auto w-full max-w-sm lg:ml-auto" data-id="element-314" data-mp="2p7nzo4rm9g0k">
              <DoodleSquiggle className="pointer-events-none absolute -top-7 right-2 hidden h-6 w-24 sm:block" color="var(--bcm-dark-blue)" data-id="element-315" data-mp="2k8ktggzwh3r0" />
              <FloatingPhoto range={5} duration={8.5} delay={0.5} data-id="element-316" data-mp="2u57gncfp86w7">
                <PhotoFrame photo={typicalWeekPhoto} ratio="aspect-[16/9]" rotate="rotate-1" className="transition-transform duration-300 hover:rotate-0" data-id="element-317" data-mp="26ea8mar0xtq8" />
              </FloatingPhoto>
            </Reveal> : null}
        </div>

        <ul className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-318" data-mp="2bcln4ys9kses">
          {typicalWeekCards.map((card, index) => {
          const recurring = findVisibleRecurring(card.recurringId);
          if (!recurring) return null;
          return <Reveal key={card.id} as="li" delay={stagger(index)} className="flex h-full flex-col rounded-[20px] border-2 border-brand-navy bg-white p-6 transition-transform duration-200 hover:-translate-y-1" data-id="element-319" data-mp="3ucujd84cmdqh" data-mpi={index}>
                <span className={`w-fit rounded-full px-3 py-1 font-meta text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-navy ${labelAccents[card.id] ?? 'bg-brand-blue'}`} data-id="element-320" data-mp="3pdrf3pw70bjk">
                  {card.label}
                </span>
                <h3 className="mt-4 font-display text-xl font-bold leading-snug text-brand-navy sm:text-2xl" data-id="element-321" data-mp="7xt39z9xzs4p">
                  {card.title}
                </h3>

                <p className="mt-4 flex items-start gap-2 font-meta text-[13px] font-semibold text-brand-navy" data-id="element-322" data-mp="c0y2nsyq7dp4">
                  <ClockIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" data-id="element-323" data-mp="2spv4r2rcmcgs" />
                  {recurring.day}s at {recurring.time}
                </p>

                {card.copy ? <p className="mt-3 font-sans text-[15px] leading-relaxed text-ink" data-id="element-324" data-mp="3cy1tl8qh7544">{card.copy}</p> : null}

                <div className="mt-4 flex items-start gap-2" data-id="element-325" data-mp="335w1lz084nox">
                  <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy" data-id="element-326" data-mp="334cm4ame6go7" />
                  <ul className="space-y-2 font-meta text-[13px] text-ink" data-id="element-327" data-mp="3vu1nwq6xekqv">
                    {recurring.locations.map(location => <li key={location.text} data-id="element-328" data-mp="3qtx2qmv2p1ub">
                        {location.label ? <span className="block font-semibold text-brand-navy" data-id="element-329" data-mp="3lyzz5vgkbkot">
                            {location.label}
                          </span> : null}
                        {location.text}
                      </li>)}
                  </ul>
                </div>

                {recurring.note ? <p className="mt-3 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-330" data-mp="lz5ev6esanr4">
                    {recurring.note}
                  </p> : null}

                {card.link ? <div className="mt-5 pt-1" data-id="element-331" data-mp="uy0mmmxh85wj">
                    <a href={card.link.href} target="_blank" rel="noopener noreferrer" className="font-meta text-[13px] font-semibold text-brand-navy underline underline-offset-4 hover:no-underline" data-id="element-332" data-mp="uwzdwp3xnvhz">
                      {card.link.label}
                      <NewTabHint data-id="element-333" data-mp="1dqcwduqgm9gp" />
                    </a>
                  </div> : null}
              </Reveal>;
        })}
        </ul>
      </Container>
    </Section>;
}